import React, { Suspense, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import './SipSummaryCard.scss';

import PaymentIcon from '@mui/icons-material/CreditCard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MonthCurrencyIcon from '../../public/images/monthly_currency.png'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { UserLoginContext } from '../../../contexts/LoggedInUserContext';
import { checkFrequency, getCurrencySymbol, getOrdinalSuffix, handleKettoLinks, handleRupee, handleUserStatus, isValidDate, onPortFolioUpdate, setCauseCart } from '../../../services/helperFunctions';
import { multiCauseSubs, sendEvents } from '../../../redux/actions/Home';
import { pauseSubscription } from '../../../redux/actions/InactiveUser';
import { SIP_STATUS } from '../SipDetailsV2/SipDetailV2';
import { Modal } from '@material-ui/core';
import lazyRetry from '../../../services/lazyRetry';

import EducationBanner from '../../public/images/sip-select/summary/education.png';
import AnimalsBanner from '../../public/images/sip-select/summary/animals.png';
import WomenGirlsBanner from '../../public/images/sip-select/summary/women_girls.png';
import FoodHungerBanner from '../../public/images/sip-select/summary/food_hunger.png';
import MedicalBanner from '../../public/images/sip-select/summary/medical.png';
import WidowBanner from '../../public/images/sip-select/summary/widow.png';
import ElderyBanner from '../../public/images/sip-select/summary/elderly.png';
import AcidVictimsBanner from '../../public/images/sip-select/summary/acid_victims.png';

const UpdatePaymentForm = React.lazy(() => lazyRetry(() => import('../PaymentInfo')));

// Image data configuration for different causes
const summaryImages = {
  education:
    { path: EducationBanner, alt: 'Banner_image' },
  animals: { path: AnimalsBanner, alt: 'Banner_image' },
  women_girls: { path: WomenGirlsBanner, alt: 'Banner_image' },
  food_hunger: { path: FoodHungerBanner, alt: 'Banner_image' },
  medical: { path: MedicalBanner, alt: 'Banner_image' },
  widow: { path: WidowBanner, alt: 'Banner_image' },
  elderly: { path: ElderyBanner, alt: 'Banner_image' },
  acid_victims: { path: AcidVictimsBanner, alt: 'Banner_image' },
};


const SipSummaryCard = ({ campaign_cause, causeDetail, sipStatus, handleOpenPledgeMoreDialog }) => {

  const { userData, latestOrder, setLatestOrder, multiArray, setMultiArray } = useContext(UserLoginContext);

  const { status_flag } = causeDetail;

  // State
  const [type, setType] = useState("");
  const [summaryImage, setSummaryImage] = useState("");
  const [updatePaymentModal, setUpdatePaymentModal] = useState(false);

  useEffect(() => {
    setType(handleUserStatus(status_flag));
    setSummaryImage(summaryImages[campaign_cause])
  }, [campaign_cause, causeDetail, latestOrder])

  const {
    latestorder, payment_mode, amount, currency
  } = latestOrder;
  const { sip_cause } = causeDetail;
  const { donation_preference } = userData;
  const emanAcc = latestorder && latestorder.payment_details && latestorder?.payment_details?.account_number;


  //Methods
  const handleResumeSip = async () => {
    const data = {
      months: '',
      status: 1,
    };
    const entityId = userData?.id;
    const subId = latestOrder.id;
    sendEvents('resume_monthly_clicked', 'sip_returning', 'homepage', type === 'cReq' ? 'impact_5' : 'impact_4');

    if (type === 'cReq') {
      pauseSubscription(entityId, subId, data).then((res) => {
        if (res.status == 200) {
          sessionStorage.removeItem('isInactive');
          setTimeout(() => {
            multiCauseSubs(entityId, userData?.latest_subscription?.currency, [], setMultiArray);
            onPortFolioUpdate(multiArray, causeDetail?.id).then((response) => {
              setLatestOrder(response);
            })
          }, 500);
        }
      });
    }
  };

  const handleReactivateSip = async () => {
    setUpdatePaymentModal(true);
  }

  const handlePaymentChange = () => {
    sessionStorage.removeItem('isInactive');
    sendEvents('update_payment_clicked', 'sip_returning', 'resume_popup', type === 'cReq' ? 'impact_5' : 'impact_4');
    setCauseCart(multiArray, campaign_cause);
    window.location.href = handleKettoLinks(`new/sip/change-payment?pr=c&email=${userData.email}&sip_cause=${campaign_cause}`);
  };

  let preferenceDonationDate = "";

  /**
 * Validates date string in 'DD/MM/YYYY' format strictly.
 */
  function isValidDate(dateString) {
    if (typeof dateString !== 'string') return false;

    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateString.match(regex);

    if (!match) return false;

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    const date = new Date(year, month - 1, day);

    // Check for valid date values
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }

  /**
   * Converts date string array JSON to "Xth of every month" format.
   */
  function getEveryMonthText(jsonDates) {
    if (!jsonDates) return null;

    let dates;
    try {
      dates = JSON.parse(jsonDates);
    } catch (e) {
      console.error('Invalid JSON:', e);
      return null;
    }

    const validDateStr = dates?.find(isValidDate);

    if (!validDateStr) return null;

    const day = parseInt(validDateStr.split('/')[0], 10);

    return `${day}${getOrdinalSuffix(day)} of every month`;
  }

  const dateStr = latestOrder?.next_payment_date;
  const parsedDate = dayjs(dateStr);
  if (parsedDate.isValid()) {
    const day = parsedDate.date();
    preferenceDonationDate = `${day}${getOrdinalSuffix(day)} of every month`;
  } else {
    preferenceDonationDate = '';
  }

  return (
    <div className="sip-summary-card">
      <div className="sip-header">
        <div className="sip-info">
          <div className='sumaary-wrapper'>
            <p className='summary-title'>{`${sip_cause.info_2} SIP`}</p>
            <p className='summary-subtitle'>{sip_cause.info_1}</p>
          </div>
          <SipStatusUI label={sipStatus} />
        </div>
        <div className="sip-image">
          <img src={summaryImage.path} alt={summaryImage.alt} />
        </div>
      </div>

      <SipActionUI sipStatus={sipStatus} causeDetail={causeDetail} payment_mode={payment_mode} emanAcc={emanAcc} latestorder={latestorder} handleResumeSip={handleResumeSip} handleReactivateSip={handleReactivateSip} handlePaymentChange={handlePaymentChange} />

      <div className="sip-body">
        <div className="sip-item">
          <div className="item-text">
            <p className="label"><PaymentIcon className="icon" />Payment details</p>
            <div className='lower-wrapper'>
              {payment_mode === 'CARD' ? (
                <p className="value mb-0">
                  card ending
                  {' '}
                  ****
                  {'  '}
                  {latestorder && latestorder.payment_details && latestorder?.payment_details?.last4}
                  &nbsp;&nbsp;&nbsp;
                  {/* {latestorder && latestorder.payment_details && latestorder?.payment_details?.exp_month && latestorder?.payment_details?.exp_year && `${latestorder?.payment_details?.exp_month} / ${String(latestorder?.payment_details?.exp_year).slice(2, 4)}`} */}
                </p>
              ) : payment_mode === 'UPIRECURRING' || payment_mode === 'UPI' ? (
                <p className="mb-0 fs-12">
                  <span className="text-bold fs-12 lh-18" style={{ color: '#626262' }}>UPI</span>
                  &nbsp;&nbsp;
                  {latestorder && latestorder.payment_details && latestorder?.payment_details?.vpa}
                </p>
              )
                : payment_mode === 'Emandate' && emanAcc
                  ? (
                    <p className="mb-0 fs-12">
                      <span className="text-black fs-12 lh-18" style={{ color: '#626262' }}>A/c</span>
                      &nbsp;&nbsp;
                      {emanAcc && emanAcc[0]}
                      {emanAcc && emanAcc.slice(1, emanAcc.length - 4).split('').map((a) => 'x')}
                      {emanAcc && emanAcc[emanAcc.length - 4]}
                      {emanAcc && emanAcc[emanAcc.length - 3]}
                      {emanAcc && emanAcc[emanAcc.length - 2]}
                      {emanAcc && emanAcc[emanAcc.length - 1]}
                      {' '}
                    </p>
                  )
                  : payment_mode === 'KETTO_WALLET' ? (
                    <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={!isAppUser ? () => history('/mywallet') : null}>
                      <img src={WalletIcon} alt="wallet-icon" style={{ width: '13px', height: '13px' }} />
                      <p className="mb-0 fs-12" style={{ color: '#204B6B' }}>
                        Ketto Wallet (
                        {' '}
                        {currSymbol}
                        {' '}
                        {userData?.wallet_balance && userData?.wallet_balance.balance && Math.round(Number(userData?.wallet_balance.balance))}
                        {' '}
                        )
                      </p>
                    </div>

                  )
                    : ''}
              {sipStatus === SIP_STATUS.LAPSED && <span className="action" onClick={handlePaymentChange}>CHANGE</span>}
            </div>
          </div>
        </div>

        {sipStatus === SIP_STATUS.ACTIVE && (<><div className='item-divider'></div>
          <div className="sip-item">
            <div className="item-text">
              <p className="label"><CalendarMonthIcon className="icon" />Upcoming Deduction</p>
              <div className='lower-wrapper'>
                <p className="value">{dayjs(latestOrder?.next_payment_date).format('DD/MM/YYYY')}</p>
                {/* <span className="action">CHANGE</span> */}
              </div>
            </div>
          </div></>)}

        <div className='item-divider'></div>

        <div className="sip-item">
          <div className="item-text">
            <p className="label"><img src={MonthCurrencyIcon} alt="monthly_currency" className="icon" />Donation</p>
            <div className='lower-wrapper'>
              <p className="value">
                {getCurrencySymbol(latestOrder?.currency)}
                {currency.toLowerCase() == 'inr' ? handleRupee(amount) : amount}
                {' '}
                {checkFrequency(latestOrder?.frequency) ? checkFrequency(latestOrder?.frequency) : '/ mo'}
              </p>
              {sipStatus === SIP_STATUS.ACTIVE && <span className="action" onClick={handleOpenPledgeMoreDialog}>{currency === "inr" ? "INCREASE FREQUENCY" : "INCREASE AMOUNT"}</span>}
            </div>
          </div>
        </div>
      </div>

      <Modal open={updatePaymentModal} onClose={setUpdatePaymentModal} className="telecall-modal modal-center mob-modal-bottom">
        <Suspense fallback={<div></div>}>
          <UpdatePaymentForm
            userData={userData}
            clearSession={() => handlePaymentChange()}
            handleClose={() => {
              sendEvents('close_popup', 'sip_returning', 'resume_popup', type === 'cReq' ? 'impact_5' : 'impact_4');
              setUpdatePaymentModal(false);
            }} />
        </Suspense>
      </Modal>
    </div>
  );
};

export default SipSummaryCard;

// micro UIs

export const SipStatusUI = ({ label }) => (
  <div className={`status ${label?.toLowerCase()}`}>
    <span className="dotCircle"></span>
    <span className="text">
      {label}
    </span>
  </div>
);

export const SipActionUI = ({ sipStatus, causeDetail, payment_mode, emanAcc, latestorder, handleResumeSip, handleReactivateSip, handlePaymentChange }) => {
  const daysUntilNextPayment = (nextPaymentDateStr) => {
    const nextPaymentDate = dayjs(nextPaymentDateStr);
    const today = dayjs();
    const days = nextPaymentDate?.diff(today, 'day');
    return days || 0;
  }
  return (
    <>
      {sipStatus === SIP_STATUS.ACTIVE && ""}
      {sipStatus === SIP_STATUS.PAUSED &&
        <div className='action-section'>
          {daysUntilNextPayment(causeDetail?.next_payment_date) > 0 &&
            <p>your SIP resumes in <span>{daysUntilNextPayment(causeDetail?.next_payment_date)}</span> days</p>
          }
          <button className='btn' onClick={handleResumeSip}>Resume Now <span><ArrowForwardIcon className="btn-arrow fs-17" /></span></button>
          <div className='item-divider-upper'></div>
        </div>}
      {sipStatus === SIP_STATUS.CANCELLED &&
        <div className='action-section'>
          <p>Cancelled on <span>{dayjs(causeDetail.paymentMissing).format('DD/MM/YYYY')}</span></p>
          <button className='btn' onClick={handleReactivateSip}>Reactivate <span><ArrowForwardIcon className="btn-arrow fs-17" /></span></button>
          <div className='item-divider-upper'></div>
        </div>}
      {sipStatus === SIP_STATUS.LAPSED &&
        <div className='action-section'>
          <div className='lapse-section'>
            <p className='title'>Payment issue with:</p>
            <div className='lower-wrapper'>
              {payment_mode === 'CARD' ? (
                <p className="value mb-0">
                  card ending
                  {' '}
                  ****
                  {'  '}
                  {latestorder && latestorder.payment_details && latestorder?.payment_details?.last4}
                  &nbsp;&nbsp;&nbsp;
                  {/* {latestorder && latestorder.payment_details && latestorder?.payment_details?.exp_month && latestorder?.payment_details?.exp_year && `${latestorder?.payment_details?.exp_month} / ${String(latestorder?.payment_details?.exp_year).slice(2, 4)}`} */}
                </p>
              ) : payment_mode === 'UPIRECURRING' || payment_mode === 'UPI' ? (
                <p className="mb-0 fs-12">
                  <span className="text-bold fs-12 lh-18" style={{ color: '#626262' }}>UPI</span>
                  &nbsp;&nbsp;
                  {latestorder && latestorder.payment_details && latestorder?.payment_details?.vpa}
                </p>
              )
                : payment_mode === 'Emandate' && emanAcc
                  ? (
                    <p className="mb-0 fs-12">
                      <span className="text-black fs-12 lh-18" style={{ color: '#626262' }}>A/c</span>
                      &nbsp;&nbsp;
                      {emanAcc && emanAcc[0]}
                      {emanAcc && emanAcc.slice(1, emanAcc.length - 4).split('').map((a) => 'x')}
                      {emanAcc && emanAcc[emanAcc.length - 4]}
                      {emanAcc && emanAcc[emanAcc.length - 3]}
                      {emanAcc && emanAcc[emanAcc.length - 2]}
                      {emanAcc && emanAcc[emanAcc.length - 1]}
                      {' '}
                    </p>
                  )
                  : payment_mode === 'KETTO_WALLET' ? (
                    <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={!isAppUser ? () => history('/mywallet') : null}>
                      <img src={WalletIcon} alt="wallet-icon" style={{ width: '13px', height: '13px' }} />
                      <p className="mb-0 fs-12" style={{ color: '#204B6B' }}>
                        Ketto Wallet (
                        {' '}
                        {currSymbol}
                        {' '}
                        {userData?.wallet_balance && userData?.wallet_balance.balance && Math.round(Number(userData?.wallet_balance.balance))}
                        {' '}
                        )
                      </p>
                    </div>
                  )
                    : ''}
            </div>
          </div>
          <button className='btn' onClick={handlePaymentChange}>Resolve <span><ArrowForwardIcon className="btn-arrow fs-17" /></span></button>
          <div className='item-divider-upper '></div>
        </div>}
    </>
  )
};
